import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth";
import { setSelectedOrgId, getSelectedOrgId } from "../api";
import { Icons } from "./icons";

/**
 * Selector de organización activa. Tres modos según el usuario:
 *   - platform admin → dropdown con "Todas las organizaciones" + cada org
 *   - miembro de N orgs → dropdown con sus orgs
 *   - miembro de 1 org → badge fijo (no se puede cambiar)
 *
 * Custom dropdown (no <select> nativo) para look consistente y mostrar
 * icono + nombre + caret. Cierra al click fuera y con Escape.
 */
export function OrgSelector() {
  const { user } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(getSelectedOrgId());
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sync = () => setSelectedId(getSelectedOrgId());
    window.addEventListener("sovra:org-changed", sync);
    return () => window.removeEventListener("sovra:org-changed", sync);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  const orgs = user.available_orgs ?? [];

  function selectOrg(id: string | null) {
    setSelectedOrgId(id);
    setSelectedId(id);
    setOpen(false);
  }

  // Mode 1: 1 org → badge fijo (no es dropdown)
  if (!user.is_platform_admin && orgs.length === 1) {
    return (
      <span className="org-pill">
        <Icons.Cube size={12} sw={1.8} />
        {orgs[0].name}
      </span>
    );
  }

  // Mode 2: 0 orgs y no es admin → nada
  if (!user.is_platform_admin && orgs.length === 0) return null;

  // Mode 3: platform admin O multi-org → dropdown custom
  const selected = orgs.find((o) => o.org_id === selectedId);
  const label = user.is_platform_admin
    ? (selected?.name ?? "Todas las organizaciones")
    : (selected?.name ?? orgs[0]?.name ?? "");

  return (
    <div ref={rootRef} className="org-picker">
      <button
        type="button"
        className="org-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Icons.Cube size={12} sw={1.8} />
        <span className="org-picker-label">{label}</span>
        <Icons.ChevronDown size={12} sw={2} />
      </button>

      {open && (
        <ul className="org-picker-menu" role="listbox">
          {user.is_platform_admin && (
            <li
              role="option"
              aria-selected={selectedId === null}
              className={`org-picker-item ${selectedId === null ? "active" : ""}`}
              onClick={() => selectOrg(null)}
            >
              <span className="org-picker-item-label">Todas las organizaciones</span>
              <span className="org-picker-item-meta">global</span>
            </li>
          )}
          {orgs.map((o) => (
            <li
              key={o.org_id}
              role="option"
              aria-selected={selectedId === o.org_id}
              className={`org-picker-item ${selectedId === o.org_id ? "active" : ""}`}
              onClick={() => selectOrg(o.org_id)}
            >
              <span className="org-picker-item-label">{o.name}</span>
              <span className="org-picker-item-meta mono">{o.slug}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
